import { Outlet, useLocation, useRouter } from '@tanstack/react-router';
import { GetShipmentsFilterStatusesItem as ShipmentFilterStatus } from '@uturn/api/v2';
import { Page } from '@uturn/portal/components/page';
import { useFacetSearch } from '@uturn/portal/hooks/queries/meilisearch';
import useShipmentViews, {
	ShipmentView,
} from '@uturn/portal/hooks/shipments/use-shipment-view';
import {
	ShipmentsCancelModal,
	ShipmentsExportModal,
} from '@uturn/portal/modules/shipments/components';
import { NON_CANCELLABLE_BULK_STATUSES } from '@uturn/portal/modules/shipments/components/shipments-cancel/shipments-cancel';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import { SHIPMENTS_STATUS_TABS } from '@uturn/portal/types/shipments';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { EventsHistorySheet } from '../components/shipments-sheet';
import { ShipmentViewProvider } from '../providers/shipment-view';

export function ShipmentsOverviewLayout() {
	const { t } = useTranslation();
	const { shipmentView, getShipmentViewTabs } = useShipmentViews();
	const { totalShipments, initTabs, tabs, statuses, shipmentsSearchFilters } =
		useShipmentsSearchStore();
	const pathname = useLocation({
		select: (location) => location.pathname,
	});

	// TODO: Improve this without useEffect.
	//       Move this to on tab click.
	useEffect(() => {
		initTabs(t, SHIPMENTS_STATUS_TABS, pathname);
	}, [pathname]);

	const showShipmentsCancelModal = !(
		pathname.includes('/archived') || pathname.includes('/groups')
	);

	const activeFilterStatuses =
		shipmentsSearchFilters?.['filter.statuses'] ?? statuses;
	const emptyStatusSelection =
		Array.isArray(shipmentsSearchFilters?.['filter.statuses']) &&
		shipmentsSearchFilters['filter.statuses'].length === 0;
	const statusFilter =
		activeFilterStatuses.length > 0
			? [`shipmentStatus IN [${activeFilterStatuses.join(',')}]`]
			: [];
	const facetSearch = useFacetSearch('shipmentStatus', undefined, statusFilter);
	const nonCancellableCount =
		facetSearch.data?.facetHits
			.filter((hit) =>
				NON_CANCELLABLE_BULK_STATUSES.includes(
					hit.value as ShipmentFilterStatus,
				),
			)
			.reduce((sum, hit) => sum + hit.count, 0) ?? 0;
	const noCancellableShipments =
		totalShipments > 0 && totalShipments - nonCancellableCount <= 0;
	const isCancellingDisabled = noCancellableShipments || emptyStatusSelection;

	const [openExport, setOpenExport] = useState(false);
	const [openEvents, setOpenEvents] = useState(false);
	const [openCancel, setOpenCancel] = useState(false);

	const router = useRouter();

	return (
		<>
			<Helmet title={t('pages.shipments.title', 'Shipments').toString()} />
			<Page
				title={t('pages.shipments.title', 'Shipments') ?? ''}
				tabsStart={tabs}
				tabsEnd={pathname.includes('groups') ? [] : getShipmentViewTabs()}
				contextActions={
					shipmentView === ShipmentView.Table
						? undefined
						: [
								{
									label: t('pages.shippers.actions.export', 'Export'),
									onClick: () => setOpenExport(true),
								},
								{
									label: t('pages.shippers.actions.cancel', 'Cancel'),
									onClick: () => setOpenCancel(true),
									renderCondition: showShipmentsCancelModal,
									disabled: isCancellingDisabled,
									disabledTooltip: isCancellingDisabled
										? t(
												'pages.shipments_cancel.dropdown.no-cancellable-disabled',
												'No shipments in the current view can be cancelled.',
											)
										: undefined,
								},
							]
				}
				primaryAction={{
					label: t('pages.shippers.actions.add', 'Create shipment'),
					onClick: () => router.navigate({ to: '/shipments/add' }),
				}}
				secondaryAction={{
					label: t('pages.shippers.actions.events', 'Events'),
					className: 'block md:hidden',
					onClick: () => setOpenEvents(true),
				}}
			>
				<EventsHistorySheet
					openSheet={openEvents}
					setOpenSheet={setOpenEvents}
				/>
				<ShipmentsExportModal
					exportFilters={shipmentsSearchFilters}
					isOpenModal={openExport}
					setOpenModal={setOpenExport}
					totalShipments={totalShipments}
				/>
				{showShipmentsCancelModal && (
					<ShipmentsCancelModal
						exportFilters={shipmentsSearchFilters}
						statuses={statuses}
						isOpenModal={openCancel}
						setOpenModal={setOpenCancel}
						nonCancellableCount={nonCancellableCount}
					/>
				)}
				<ShipmentViewProvider view={shipmentView}>
					<Outlet />
				</ShipmentViewProvider>
			</Page>
		</>
	);
}
